<template>
  <div>
    <!-- Print Controls (hidden when printing) -->
    <div class="print:hidden flex flex-col sm:flex-row justify-end gap-4 mb-6">
      <select
        v-model="selectedPageSize"
        class="rounded border-gray-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <optgroup label="Standard Paper Sizes">
          <option v-for="size in standardSizes" :key="size.value" :value="size.value">
            {{ size.label }}
          </option>
        </optgroup>
        <optgroup label="Thermal Receipt Printers">
          <option v-for="size in thermalSizes" :key="size.value" :value="size.value">
            {{ size.label }}
          </option>
        </optgroup>
      </select>

      <button
        @click="printReceipt"
        class="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2 rounded shadow transition"
      >
        Print Receipt
      </button>
    </div>

    <!-- Printable Area -->
    <div id="printable-receipt" class="bg-white">

      <!-- ====== A4 / A5 / Letter Layout ====== -->
      <div v-if="!isThermal" class="px-8 py-12 max-w-4xl mx-auto text-sm leading-relaxed">

        <!-- ── Facility Header ──────────────────────────────────────────────── -->
        <div class="text-center pb-8 mb-8 border-b-2 border-gray-800">
          <!-- Logo — centred, own line, doubled from h-20 to h-40 -->
          <div class="flex justify-center mb-8">
            <img
              v-if="facility.logo_url"
              :src="facility.logo_url"
              :alt="facility.FacilityName"
              class="h-40 w-auto max-w-[300px] object-contain"
            />
            <!-- Placeholder: simple building icon when no logo is set -->
            <div
              v-else
              class="h-40 w-40 rounded border border-gray-300 bg-gray-50 flex items-center justify-center"
            >
              <svg class="w-20 h-20 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M3 21V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14M3 21h18M9 21V11h6v10"/>
              </svg>
            </div>
          </div>

          <!-- Facility details — centred below the logo -->
           <div class="flex justify-around">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ facility.FacilityName || "Health Facility" }}</h1>
              <p v-if="facilityType" class="text-sm text-gray-500 mt-1">{{ facilityType }}</p>
              <p v-if="facilityLocation" class="text-gray-600 mt-1">{{ facilityLocation }}</p>
              <p v-if="facility.e_tims_no && facility.e_tims_no !== 'N/A'" class="text-gray-500 text-xs mt-1">
                eTIMS No: {{ facility.e_tims_no }}
              </p>
            </div>
            <div>
              <!-- Receipt label + print time — below facility details -->
              <p class="text-2xl font-bold text-gray-800 uppercase tracking-widest mt-3">Receipt</p>
              <p class="text-xs text-gray-500 mt-1">Printed: {{ new Date().toLocaleString("en-KE") }}</p>
            </div>
           </div>

        </div>
        <!-- ── /Facility Header ─────────────────────────────────────────────── -->

        <!-- Patient & Invoice Info -->
        <div class="grid grid-cols-2 gap-6 mb-10 text-gray-700">
          <div>
            <p class="text-base"><strong>Patient Name:</strong> {{ billDetails.patient?.FullName || "N/A" }}</p>
            <p><strong>Patient Code:</strong> {{ billDetails.patient?.unique_code || "N/A" }}</p>
            <p><strong>Patient Phone:</strong> {{ billDetails.patient?.PhoneNumber || "N/A" }}</p>
          </div>
          <div>
            <p class="text-base"><strong>ID Number:</strong> {{ billDetails.patient?.IdNumber || "N/A" }}</p>
            <p><strong>Invoice No:</strong> {{ billDetails.bill_number }}</p>
            <p><strong>Date:</strong> {{ formatDate(billDetails.created_at) }}</p>
          </div>
        </div>

        <!-- Items Table -->
        <table class="w-full border-collapse text-left text-sm mb-10">
          <thead class="bg-gray-100 text-gray-900">
            <tr>
              <th class="py-3 px-4 font-bold">Item / Service</th>
              <th class="py-3 px-4 text-right font-bold hidden sm:table-cell">Units</th>
              <th class="py-3 px-4 text-right font-bold hidden sm:table-cell">Unit Price</th>
              <th class="py-3 px-4 text-right font-bold">Amount</th>
              <th v-if="hasAnyDiscount" class="py-3 px-4 text-right font-bold text-red-600">Discount</th>
              <th class="py-3 px-4 text-right font-bold hidden sm:table-cell">Paid Via</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <template v-for="(item, i) in allBillItems" :key="i">
              <!-- Category group header row -->
              <tr v-if="isCategoryHeader(item, i)" class="bg-gray-50 font-bold text-gray-800">
                <td :colspan="hasAnyDiscount ? 6 : 5" class="py-3 px-4">
                  {{ getCategoryName(item.billing_category) }}
                </td>
              </tr>
              <!-- Bill item row -->
              <tr>
                <td class="py-4 px-4">{{ getItemName(item) }}</td>
                <td class="py-4 px-4 text-right hidden sm:table-cell">{{ item.units }}</td>
                <td class="py-4 px-4 text-right hidden sm:table-cell">{{ formatCurrency(item.unit_price) }}</td>
                <td class="py-4 px-4 text-right font-medium">{{ formatCurrency(item.total_amount) }}</td>
                <td v-if="hasAnyDiscount" class="py-4 px-4 text-right text-red-600">
                  {{ parseFloat(item.discount) > 0 ? '- ' + formatCurrency(item.discount) : '—' }}
                </td>
                <td class="py-4 px-4 text-right text-xs hidden sm:table-cell">{{ item.paid_via || '—' }}</td>
              </tr>
            </template>
          </tbody>

          <tfoot>
            <!-- Original total -->
            <tr class="border-t-4 border-double border-gray-800">
              <td :colspan="hasAnyDiscount ? 4 : 3" class="pt-4 pb-1 text-right text-gray-700">
                Original Amount
              </td>
              <td class="pt-4 pb-1 text-right font-medium">{{ formatCurrency(billDetails.total_amount) }}</td>
              <td v-if="hasAnyDiscount"></td>
            </tr>

            <!-- Discount row -->
            <tr v-if="billDiscount > 0">
              <td :colspan="hasAnyDiscount ? 4 : 3" class="py-1 text-right text-red-600 font-medium">
                Discount Applied
              </td>
              <td class="py-1 text-right text-red-600 font-medium">- {{ formatCurrency(billDiscount) }}</td>
              <td v-if="hasAnyDiscount"></td>
            </tr>

            <!-- Amount payable after discount -->
            <tr v-if="billDiscount > 0" class="border-t border-gray-400">
              <td :colspan="hasAnyDiscount ? 4 : 3" class="py-1 text-right font-bold text-gray-800">
                Amount Payable
              </td>
              <td class="py-1 text-right font-bold text-gray-800">{{ formatCurrency(amountPayable) }}</td>
              <td v-if="hasAnyDiscount"></td>
            </tr>

            <!-- Amount paid -->
            <tr>
              <td :colspan="hasAnyDiscount ? 4 : 3" class="py-1 text-right text-gray-700">Amount Paid</td>
              <td class="py-1 text-right font-medium text-green-700">{{ formatCurrency(billDetails.amount_paid) }}</td>
              <td v-if="hasAnyDiscount"></td>
            </tr>

            <!-- Balance remaining -->
            <tr v-if="billBalance > 0" class="border-t border-gray-400">
              <td :colspan="hasAnyDiscount ? 4 : 3" class="py-1 text-right font-bold text-gray-800">
                Balance Remaining
              </td>
              <td class="py-1 text-right font-bold text-orange-600">{{ formatCurrency(billBalance) }}</td>
              <td v-if="hasAnyDiscount"></td>
            </tr>

            <!-- Paid in full banner -->
            <tr v-else class="border-t border-gray-400">
              <td :colspan="hasAnyDiscount ? 6 : 5" class="py-3 text-center">
                <span class="inline-block bg-green-100 text-green-800 font-bold text-sm px-6 py-1 rounded-full border border-green-400">
                  ✓ PAID IN FULL
                </span>
              </td>
            </tr>
          </tfoot>
        </table>

        <!-- Footer -->
        <div class="text-center text-xs text-gray-500 mt-16 border-t pt-6">
          <p class="font-medium">Thank you for choosing {{ facility.FacilityName || "our facility" }}.</p>
          <p class="mt-1">Powered by Jiji Health • Printed on {{ new Date().toLocaleString("en-KE") }}</p>
        </div>
      </div>

      <!-- ====== Thermal Receipt Layout (58mm / 80mm) ====== -->
      <div v-else class="font-mono text-xs leading-tight" :style="thermalStyle">

        <!-- Thermal Header -->
        <div class="text-center border-b-2 border-dashed border-black pb-3 mb-3">
          <!--
            Thermal printers are monochrome and cannot render images reliably.
            We always show text-only in thermal mode for maximum compatibility.
          -->
          <h2 class="text-base font-bold">{{ facility.FacilityName || "HEALTH FACILITY" }}</h2>
          <p v-if="facilityLocation" class="text-xs mt-0.5">{{ facilityLocation }}</p>
          <p v-if="facility.e_tims_no && facility.e_tims_no !== 'N/A'" class="text-xs">
            eTIMS: {{ facility.e_tims_no }}
          </p>
        </div>

        <!-- Thermal: Patient / Invoice info -->
        <div class="mb-3">
          <div class="flex justify-between">
            <span>Invoice:</span>
            <strong>{{ billDetails.bill_number }}</strong>
          </div>
          <div class="flex justify-between">
            <span>Date:</span>
            {{ formatDateShort(billDetails.created_at) }}
          </div>
          <div class="flex justify-between">
            <span>Patient:</span>
            {{ billDetails.patient?.FullName || "N/A" }}
          </div>
          <div class="flex justify-between">
            <span>Phone:</span>
            {{ billDetails.patient?.PhoneNumber || "N/A" }}
          </div>
        </div>

        <div class="border-t-2 border-dashed border-black mb-3"></div>

        <!-- Thermal: Items -->
        <div v-for="(item, i) in allBillItems" :key="i" class="mb-3">
          <div class="font-bold text-xs">{{ getItemName(item) }}</div>
          <div class="flex justify-between text-xs">
            <span>{{ item.units }} × {{ formatCurrencyPlain(item.unit_price) }}</span>
            <span>{{ formatCurrencyPlain(item.total_amount) }}</span>
          </div>
          <div v-if="parseFloat(item.discount) > 0" class="flex justify-between text-xs pl-2 opacity-80">
            <span>Discount</span>
            <span>- {{ formatCurrencyPlain(item.discount) }}</span>
          </div>
          <div v-if="item.paid_via" class="text-right text-xs opacity-70">({{ item.paid_via }})</div>
        </div>

        <!-- Thermal: Totals waterfall -->
        <div class="border-t-2 border-dashed border-black pt-2">
          <div class="flex justify-between text-xs">
            <span>Original Total</span>
            <span>KES {{ formatCurrencyPlain(billDetails.total_amount) }}</span>
          </div>

          <template v-if="billDiscount > 0">
            <div class="flex justify-between text-xs">
              <span>Discount</span>
              <span>- KES {{ formatCurrencyPlain(billDiscount) }}</span>
            </div>
            <div class="border-t border-dashed border-black my-1"></div>
            <div class="flex justify-between text-xs font-bold">
              <span>Amount Payable</span>
              <span>KES {{ formatCurrencyPlain(amountPayable) }}</span>
            </div>
          </template>

          <div class="border-t border-dashed border-black my-1"></div>

          <div class="flex justify-between text-xs">
            <span>Amount Paid</span>
            <span>KES {{ formatCurrencyPlain(billDetails.amount_paid) }}</span>
          </div>

          <template v-if="billBalance > 0">
            <div class="border-t border-dashed border-black my-1"></div>
            <div class="flex justify-between text-xs font-bold">
              <span>Balance Due</span>
              <span>KES {{ formatCurrencyPlain(billBalance) }}</span>
            </div>
          </template>

          <div v-else class="text-center font-bold text-xs mt-2 border border-black py-1">
            *** PAID IN FULL ***
          </div>
        </div>

        <!-- Thermal: Footer -->
        <div class="text-center mt-5 text-xs">
          <p>Thank you for choosing</p>
          <p class="font-bold">{{ facility.FacilityName || "our facility" }}!</p>
          <p>*** Have a nice day ***</p>
        </div>
        <div class="mt-12"></div><!-- Paper cut space -->
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "ReceiptInfo",
  props: {
    billDetails: { type: Object, required: true },
  },
  data() {
    return {
      selectedPageSize: "a4",
      standardSizes: [
        { label: "A4",     value: "a4"     },
        { label: "A5",     value: "a5"     },
        { label: "Letter", value: "letter" },
      ],
      thermalSizes: [
        { label: "Thermal 58mm", value: "thermal-58" },
        { label: "Thermal 80mm", value: "thermal-80" },
      ],
    };
  },
  computed: {
    // ── Facility ──────────────────────────────────────────────────────────────
    // billing_controller.rb includes: patient: { include: { facility: {} } }
    // in both bill#show and bill#show_by_visit, so this path is always populated.
    facility() {
      return this.billDetails.patient?.facility || {};
    },
    facilityType() {
      return this.facility.Type || null;
    },
    facilityLocation() {
      const f = this.facility;
      const parts = [f.NearestTown, f.SubCounty, f.County].filter(Boolean);
      return parts.join(", ") || null;
    },

    // ── Layout ────────────────────────────────────────────────────────────────
    isThermal() {
      return this.selectedPageSize.startsWith("thermal");
    },
    thermalStyle() {
      return this.selectedPageSize === "thermal-58"
        ? { maxWidth: "280px", margin: "0 auto", padding: "10px" }
        : { maxWidth: "384px", margin: "0 auto", padding: "15px" };
    },

    // ── Bill items ────────────────────────────────────────────────────────────
    allBillItems() {
      return this.billDetails.bill_items || [];
    },

    // ── Discount & totals ─────────────────────────────────────────────────────
    hasAnyDiscount() {
      return this.allBillItems.some(item => parseFloat(item.discount) > 0);
    },
    billDiscount() {
      const billLevelDiscount = parseFloat(this.billDetails.discount) || 0;
      if (billLevelDiscount > 0) return billLevelDiscount;
      return this.allBillItems.reduce(
        (sum, item) => sum + (parseFloat(item.discount) || 0), 0
      );
    },
    amountPayable() {
      return Math.max(
        (parseFloat(this.billDetails.total_amount) || 0) - this.billDiscount,
        0
      );
    },
    billBalance() {
      return Math.max(parseFloat(this.billDetails.balance) || 0, 0);
    },
  },
  methods: {
    formatCurrency(value) {
      if (value == null) return "KES 0.00";
      return new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(value);
    },
    formatCurrencyPlain(value) {
      if (value == null) return "0.00";
      return Number(value).toFixed(2);
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      return new Date(dateStr).toLocaleDateString("en-KE", {
        year: "numeric", month: "long", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },
    formatDateShort(dateStr) {
      if (!dateStr) return "N/A";
      return new Date(dateStr).toLocaleDateString("en-KE", {
        day: "2-digit", month: "short", year: "numeric",
      });
    },
    getItemName(item) {
      return item.facility_service?.name || item.inventory_item?.trade_name || "Unknown";
    },
    getCategoryName(cat) {
      const map = { 1: "Consultation", 2: "Laboratory", 3: "Radiology", 4: "Prescription", 5: "Procedure" };
      return map[cat] || "Other";
    },
    isCategoryHeader(item, index) {
      if (index === 0) return true;
      return this.allBillItems[index - 1].billing_category !== item.billing_category;
    },
    printReceipt() {
      const style = document.createElement("style");
      style.id = "print-style-override";

      if (this.isThermal) {
        style.textContent = `
          @page { size: auto; margin: 0; }
          @media print {
            body * { visibility: hidden; }
            #printable-receipt, #printable-receipt * { visibility: visible; }
            #printable-receipt {
              position: absolute; left: 0; top: 0;
              width: ${this.selectedPageSize === "thermal-58" ? "280px" : "384px"};
            }
            html, body { margin: 0; padding: 0; width: 100%; }
          }
        `;
      } else {
        const sizeMap = { a4: "A4", a5: "A5", letter: "letter" };
        const pageSize = sizeMap[this.selectedPageSize] || "A4";
        style.textContent = `
          @page { size: ${pageSize}; margin: 1cm; }
          @media print {
            body * { visibility: hidden; }
            #printable-receipt, #printable-receipt * { visibility: visible; }
            #printable-receipt {
              position: absolute; left: 0; top: 0;
              width: 100%; min-height: 100vh;
            }
          }
        `;
      }

      document.head.appendChild(style);
      window.print();
      setTimeout(() => document.getElementById("print-style-override")?.remove(), 1000);
    },
  },
};
</script>

<style scoped>
@media print {
  .print\:hidden { display: none !important; }
}
</style>